import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Stack,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NavLink, useHistory } from 'react-router-dom';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import microsoftLogo from 'src/assets/img/microsoft.svg';
import oktaLogo from 'src/assets/img/okta.svg';
import {
  APP_EVENTS,
  captureAppEvent,
  LOGIN_METHOD,
  type LoginMethod,
} from 'src/services/posthogEvents';

import useLocalStore from 'src/store/useLocalStore';

import { Api } from 'src/models/Api.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import {
  LoginType,
  SsoParams,
} from 'src/models/GalenData/LoginUsingPOST.model';
import { AxiosResponseWithRefreshToken } from 'src/models/GalenData/Request.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { I18NextNS } from 'src/models/Language.model';

import useAutoConnectCamera from 'src/hooks/useAutoConnectCamera';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useSetCurrentUserInfo from 'src/hooks/useSetCurrentUserInfo';

import {
  generateOAuthState,
  generateRandomString,
} from 'src/utils/generateRandomString';
import { emailPattern } from 'src/utils/validationRules';

const SSO_METHOD_BY_PROVIDER: Record<LoginType, LoginMethod> = {
  [LoginType.Okta]: LOGIN_METHOD.OKTA,
  [LoginType.Microsoft]: LOGIN_METHOD.MICROSOFT,
};

type LoginFormFieldTypes = {
  email: string;
  password: string;
};

interface PasswordLoginProps {
  isLoggingIn: boolean;
  setIsLoggingIn: (value: boolean) => void;
  setCurrentPassword: (password: string) => void;
  setXRequestToken: (token: string) => void;
  setShouldShowOTPInput: (value: boolean) => void;
  validateUserRole: (
    res: AxiosResponseWithRefreshToken<GalenDataUser>,
    callback?: () => void,
  ) => boolean;

  setShouldShowResetPassword: (value: boolean) => void;
  setUserId: (userId: string) => void;
}

const PasswordLogin = ({
  isLoggingIn,
  setIsLoggingIn,
  setCurrentPassword,
  setXRequestToken,
  setShouldShowOTPInput,
  validateUserRole,
  setShouldShowResetPassword,
  setUserId,
}: PasswordLoginProps) => {
  const {
    rememberUserEmail,
    setRememberUserEmail,
    isRememberEmail,
    setIsRememberEmail,
  } = useLocalStore();

  const { setCurrentUserInfo } = useSetCurrentUserInfo();

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginFormFieldTypes>({
    defaultValues: {
      email: rememberUserEmail ?? '',
      password: '',
    },
  });
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const [isShowPassword, setIsShowPassword] = useState(false);
  const history = useHistory();
  const { handleHttpRequestError, showHttpRequestError } =
    useHandleHttpRequestError();
  const { autoConnectCamera } = useAutoConnectCamera();

  const handleLogin = async ({ email, password }: LoginFormFieldTypes) => {
    try {
      const emailTrim = email.trim();
      const passwordTrim = password.trim();

      setIsLoggingIn(true);
      setCurrentPassword(passwordTrim);

      const res = await Api.remote.login(emailTrim, passwordTrim);

      if (!validateUserRole(res)) {
        return;
      }

      const getXRequestToken = res.headers['x-request-token'];
      setXRequestToken(getXRequestToken);

      if (!res.data) {
        setShouldShowOTPInput(true);
        return;
      }

      await setCurrentUserInfo(res);

      if (res.data?.passwordExpired) {
        setUserId(res.data.userId);
        setShouldShowResetPassword(true);
        return;
      }

      captureAppEvent(APP_EVENTS.LOGIN_SUCCEEDED, {
        method: LOGIN_METHOD.PASSWORD,
        mfa_used: false,
      });

      autoConnectCamera();

      if (isRememberEmail) {
        setRememberUserEmail(emailTrim);
      } else {
        setRememberUserEmail(undefined);
      }

      history.replace('/');
    } catch (err) {
      captureAppEvent(APP_EVENTS.LOGIN_FAILED, {
        method: LOGIN_METHOD.PASSWORD,
      });
      showHttpRequestError(err);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const { ssoState, setSsoState } = useLocalStore();

  const [isLoginWithSso, setIsLoginWithSso] = useState(false);

  const handleLogout = useCallback(
    (provider: LoginType) => {
      if (provider === LoginType.Okta) {
        window.location.href = `${import.meta.env.VITE_OKTA_CLIENT_DOMAIN}/login/signout?fromURI=${import.meta.env.VITE_REDIRECT_URI}`;
        return;
      }

      window.history.replaceState({}, '', '/');
      history.replace('/');
    },
    [history],
  );

  const handleSsoRedirectCallback = useCallback(async () => {
    const urlParams = new URLSearchParams(location.search.split('?')[1]);

    const code = urlParams.get(SsoParams.Code);
    const state = urlParams.get(SsoParams.State);

    const decodedState = decodeURIComponent(state || '');
    const provider = decodedState.split('|')[1];

    if (code && state === ssoState) {
      try {
        setIsLoginWithSso(true);
        const res = await Api.remote.oauth2Login({
          code,
          loginType: provider as LoginType,
          redirectUri: import.meta.env.VITE_REDIRECT_URI,
        });

        if (
          !validateUserRole(res, () => {
            handleLogout(provider as LoginType);
          })
        ) {
          return;
        }

        setSsoState(generateRandomString(15));

        await setCurrentUserInfo(res);

        captureAppEvent(APP_EVENTS.LOGIN_SUCCEEDED, {
          method: SSO_METHOD_BY_PROVIDER[provider as LoginType],
        });

        autoConnectCamera();

        window.history.replaceState({}, '', '/');
        history.replace('/');
      } catch (error) {
        captureAppEvent(APP_EVENTS.LOGIN_FAILED, {
          method: SSO_METHOD_BY_PROVIDER[provider as LoginType],
        });
        if (
          error instanceof AxiosError &&
          error.response?.status === 403 &&
          error.response?.data?.includes(
            GalenErrorKeys.TENANT_DOES_NOT_ALLOW_SELF_REGISTRATION,
          )
        ) {
          return Api.alertBox({
            title: t('bootbox.title.Error', {
              ns: I18NextNS.COMMON,
            }),
            message: t('loginLogout.userNotFoundInOptain'),
            locale: language,
            type: 'error',
            callback: () => {
              handleLogout(provider as LoginType);
            },
          });
        }

        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: t('bootbox.title.Error', {
            ns: I18NextNS.COMMON,
          }),
          message: errorMessage,
          locale: language,
          type: 'error',
          callback: () => {
            handleLogout(provider as LoginType);
          },
        });
      }
    }
  }, [
    ssoState,
    validateUserRole,
    setCurrentUserInfo,
    setSsoState,
    autoConnectCamera,
    history,
    handleLogout,
    handleHttpRequestError,
    t,
    language,
  ]);

  useEffect(() => {
    handleSsoRedirectCallback();
  }, [handleSsoRedirectCallback]);

  const handleOktaLogin = useCallback(() => {
    const ssoState = generateOAuthState(LoginType.Okta);

    setSsoState(ssoState);

    const params = new URLSearchParams({
      client_id: import.meta.env.VITE_OKTA_CLIENT_ID,
      redirect_uri: import.meta.env.VITE_REDIRECT_URI,
      state: ssoState,
      response_type: SsoParams.Code,
      scope: import.meta.env.VITE_OKTA_SCOPE,
      prompt: import.meta.env.VITE_OKTA_PROMPT,
    });
    const url = `${import.meta.env.VITE_OKTA_CLIENT_DOMAIN}/oauth2/v1/authorize?${params.toString()}`;

    window.location.href = url;
  }, [setSsoState]);

  const handleMicrosoftLogin = useCallback(() => {
    const ssoState = generateOAuthState(LoginType.Microsoft);

    setSsoState(ssoState);

    const params = new URLSearchParams({
      client_id: import.meta.env.VITE_MICROSOFT_CLIENT_ID,
      response_type: SsoParams.Code,
      redirect_uri: import.meta.env.VITE_REDIRECT_URI,
      state: ssoState,
      scope: import.meta.env.VITE_MICROSOFT_SCOPE,
      prompt: import.meta.env.VITE_MICROSOFT_PROMPT,
    });

    const url = `${import.meta.env.VITE_MICROSOFT_CLIENT_DOMAIN}/common/oauth2/v2.0/authorize?${params.toString()}`;

    window.location.href = url;
  }, [setSsoState]);

  return (
    <>
      {isLoginWithSso ? (
        <div className="p-5 d-flex flex-column justify-content-center align-items-center gap-2">
          <Spinner variant="primary" />
          <p>{t('loginLogout.loggingIn')}</p>
        </div>
      ) : (
        <Form onSubmit={handleSubmit(handleLogin)} data-test="login-submit">
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>
                <strong>{t('loginLogout.fields.email')}</strong>
              </Form.Label>
              <Form.Control
                id="email"
                type="text"
                placeholder={t('loginLogout.fields.emailPlaceholder')}
                autoComplete="off"
                {...register('email', {
                  required: t('loginLogout.fields.emailRequired'),
                  pattern: {
                    value: emailPattern,
                    message: t('loginLogout.fields.emailInvalid'),
                  },
                  maxLength: {
                    value: 254,
                    message: t('loginLogout.fields.emailMaxLength'),
                  },
                })}
              ></Form.Control>

              {errors.email?.message && (
                <p className="text-danger" data-test="email-required-error">
                  {errors.email?.message}
                </p>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>
                <strong>{t('loginLogout.fields.password')}</strong>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  id="password"
                  type={isShowPassword ? 'text' : 'password'}
                  placeholder={t('loginLogout.fields.passwordPlaceholder')}
                  autoComplete="off"
                  {...register('password', {
                    required: t('loginLogout.fields.passwordRequired'),
                    minLength: {
                      value: 8,
                      message: t('loginLogout.fields.passwordMinLength'),
                    },
                    maxLength: {
                      value: 128,
                      message: t('loginLogout.fields.passwordMaxLength'),
                    },
                  })}
                />

                <InputGroup.Text
                  onClick={() => setIsShowPassword(!isShowPassword)}
                  className="pointer"
                  data-test="password-visibility-toggle"
                >
                  <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
                </InputGroup.Text>
              </InputGroup>
              {errors.password?.message && (
                <p className="text-danger" data-test="password-required-error">
                  {errors.password?.message}
                </p>
              )}
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center">
              <Form.Check
                type="checkbox"
                label={t('loginLogout.rememberEmail')}
                id="rememberEmail"
                checked={isRememberEmail}
                onChange={() => setIsRememberEmail(!isRememberEmail)}
              />

              <NavLink
                to="/forgot-password"
                className="text-primary text-decoration-none"
                data-test="login-forgot-password-link"
                data-track="auth.login.forgot-password"
              >
                {t('changePassword.forgotPassword')}
              </NavLink>
            </div>

            <div>
              {isLoggingIn && (
                <p className="alert alert-primary mb-0 mt-2">
                  {t('loginLogout.loggingInPleaseWait')}
                </p>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer className="d-block">
            <Stack gap={2}>
              <Button
                disabled={isLoggingIn}
                type="submit"
                variant="primary"
                data-track="auth.login.submit"
                data-observe
              >
                {isLoggingIn
                  ? t('loginLogout.loggingIn')
                  : t('loginLogout.login')}
              </Button>
              <p className="text-center fw-bold mb-0">{t('loginLogout.OR')}</p>
              <Button
                variant="light"
                onClick={handleMicrosoftLogin}
                data-test="login-microsoft-sign-in-button"
                data-track="auth.login.microsoft-sign-in"
                data-observe
              >
                <img
                  src={microsoftLogo}
                  alt="Microsoft"
                  width={20}
                  className="object-fit-contain me-2"
                />
                <span>{t('loginLogout.continueWithMicrosoft')}</span>
              </Button>
              <Button
                variant="light"
                onClick={handleOktaLogin}
                data-test="login-okta-sign-in-button"
                data-track="auth.login.okta-sign-in"
                data-observe
              >
                <img
                  src={oktaLogo}
                  alt="Okta"
                  width={40}
                  className="object-fit-contain me-2"
                />
                <span>{t('loginLogout.continueWithOkta')}</span>
              </Button>
            </Stack>
          </Modal.Footer>
        </Form>
      )}
    </>
  );
};

export default PasswordLogin;
